import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, Palette, Wand2, Image, Bookmark, LayoutGrid, Star, HelpCircle, Mail } from 'lucide-react';

const ActivityBar = ({ activeSection, onSectionChange, onStartOnboarding, hasNewVisualizer, hasNewSavedPalette }) => {
  const [hoveredItem, setHoveredItem] = useState(null);

  const sections = [
    {
      id: 'info',
      icon: Info,
      label: 'Color Information',
      description: 'Color history and detailed information'
    },
    {
      id: 'palettes',
      icon: Palette,
      label: 'Generated Palettes',
      description: 'Harmony-based color palettes'
    },
    {
      id: 'builder',
      icon: Wand2,
      label: 'Palette Builder',
      description: 'Build custom color palettes'
    },
    {
      id: 'extractor',
      icon: Image,
      label: 'Image Extractor',
      description: 'Extract colors from images'
    },
    {
      id: 'saved',
      icon: Bookmark,
      label: 'Saved Palettes',
      description: 'Save and manage your color palettes'
    },
    {
      id: 'visualizer',
      icon: LayoutGrid,
      label: 'Visualizer',
      description: 'Preview colors in UI designs'
    }
  ];

  return (
    <div className="fixed left-0 top-0 bottom-0 flex flex-col w-16 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 z-10">
      {/* Logo at top */}
      <div className="flex items-center justify-center py-4 border-b border-gray-200 dark:border-gray-700">
        <Link
          to="/"
          className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg shadow flex items-center justify-center overflow-hidden group hover:shadow-md transition-all duration-200 hover:scale-105"
          title="Komorebi Color Play"
        >
          <img
            src="/komorebi-logo.svg"
            alt="Komorebi Logo"
            className="w-6 h-6 object-contain filter drop-shadow-sm group-hover:scale-110 transition-transform duration-200"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          {/* Fallback icon if SVG doesn't load */}
          <div className="w-6 h-6 text-gray-700 dark:text-gray-300 font-bold text-sm hidden items-center justify-center">
            K
          </div>
        </Link>
      </div>

      {/* Activity Bar Items - Vertically centered */}
      <div className="flex flex-col items-center justify-center flex-1 space-y-2">
        {sections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          const isHovered = hoveredItem === section.id;

          return (
            <div key={section.id} className="relative">
              <button
                onClick={() => onSectionChange(section.id)}
                onMouseEnter={() => setHoveredItem(section.id)}
                onMouseLeave={() => setHoveredItem(null)}
                onMouseOver={() => setHoveredItem(section.id)}
                onMouseOut={() => setHoveredItem(null)}
                onFocus={() => setHoveredItem(section.id)}
                onBlur={() => setHoveredItem(null)}
                className={`
                  relative w-12 h-12 flex items-center justify-center rounded-lg
                  transition-all duration-200
                  ${isActive
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                  }
                `}
                aria-label={section.label}
                title={section.description}
              >
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-blue-500 dark:bg-blue-500 rounded-r-full" />
                )}

                <Icon className="w-5 h-5 relative z-10" />

                {/* NEW badge for visualizer */}
                {section.id === 'visualizer' && hasNewVisualizer && !isActive && (
                  <div className="absolute -top-0.5 -right-0.5 px-1 py-0.5 text-[8px] font-bold bg-red-500 text-white rounded-full leading-none">
                    NEW
                  </div>
                )}

                {/* NEW badge for saved palettes */}
                {section.id === 'saved' && hasNewSavedPalette && !isActive && (
                  <div className="absolute -top-0.5 -right-0.5 px-1 py-0.5 text-[8px] font-bold bg-red-500 text-white rounded-full leading-none">
                    NEW
                  </div>
                )}
              </button>

              {/* Custom tooltip with fade-in */}
              {isHovered && (
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-3 py-2 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white rounded-lg shadow-lg whitespace-nowrap animate-fadeIn pointer-events-none">
                  {section.label}
                  {/* Arrow */}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-white rotate-45 -mr-1"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom buttons */}
      <div className="flex flex-col items-center space-y-2 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
        {/* How to Use button */}
        <div className="relative">
          <button
            onClick={onStartOnboarding}
            onMouseEnter={() => setHoveredItem('help')}
            onMouseLeave={() => setHoveredItem(null)}
            onMouseOver={() => setHoveredItem('help')}
            onMouseOut={() => setHoveredItem(null)}
            onFocus={() => setHoveredItem('help')}
            onBlur={() => setHoveredItem(null)}
            className="relative w-12 h-12 flex items-center justify-center rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
            aria-label="How to Use"
          >
            <HelpCircle className="w-5 h-5 relative z-10" />
          </button>

          {/* Tooltip */}
          {hoveredItem === 'help' && (
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-3 py-2 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white rounded-lg shadow-lg whitespace-nowrap animate-fadeIn pointer-events-none">
              How to Use
              <div className="absolute right-full top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-white rotate-45 -mr-1"></div>
            </div>
          )}
        </div>

        {/* Contact button */}
        <div className="relative">
          <a
            href="mailto:hello@obelisk.agency"
            onMouseEnter={() => setHoveredItem('contact')}
            onMouseLeave={() => setHoveredItem(null)}
            onMouseOver={() => setHoveredItem('contact')}
            onMouseOut={() => setHoveredItem(null)}
            onFocus={() => setHoveredItem('contact')}
            onBlur={() => setHoveredItem(null)}
            className="relative w-12 h-12 flex items-center justify-center rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
            aria-label="Contact"
          >
            <Mail className="w-5 h-5 relative z-10" />
          </a>

          {/* Tooltip */}
          {hoveredItem === 'contact' && (
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-3 py-2 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white rounded-lg shadow-lg whitespace-nowrap animate-fadeIn pointer-events-none">
              Contact / Report Bugs
              <div className="absolute right-full top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-white rotate-45 -mr-1"></div>
            </div>
          )}
        </div>

        {/* Review on Product Hunt button */}
        <div className="relative">
          <a
            href="https://www.producthunt.com/products/komorebi-color-play/reviews?utm_source=badge-product_review&utm_medium=badge&utm_source=badge-komorebi-color-play"
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setHoveredItem('review')}
            onMouseLeave={() => setHoveredItem(null)}
            onMouseOver={() => setHoveredItem('review')}
            onMouseOut={() => setHoveredItem(null)}
            onFocus={() => setHoveredItem('review')}
            onBlur={() => setHoveredItem(null)}
            className="relative w-12 h-12 flex items-center justify-center rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
            aria-label="Review on Product Hunt"
          >
            <Star className="w-5 h-5 relative z-10" />
          </a>

          {/* Tooltip */}
          {hoveredItem === 'review' && (
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-3 py-2 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white rounded-lg shadow-lg whitespace-nowrap animate-fadeIn pointer-events-none">
              Review on Product Hunt
              <div className="absolute right-full top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-white rotate-45 -mr-1"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityBar;
